import { Laptop, Moon, Sun } from "lucide-react";
import { useTheme, type ThemeChoice } from "@/lib/theme";

const OPTIONS: { value: ThemeChoice; label: string; Icon: typeof Sun }[] = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Laptop },
];

/**
 * Minimal three-way theme selector. `variant="compact"` is the header pill;
 * `variant="rows"` is used inside the mobile drawer.
 */
export function ThemeToggle({ variant = "compact" }: { variant?: "compact" | "rows" }) {
  const { theme, setTheme } = useTheme();

  if (variant === "rows") {
    return (
      <div role="radiogroup" aria-label="Theme" className="flex flex-col gap-1">
        {OPTIONS.map(({ value, label, Icon }) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={theme === value}
            onClick={() => setTheme(value)}
            className={
              "flex items-center gap-3 py-2 text-xs font-medium uppercase tracking-[0.24em] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C6A15B] " +
              (theme === value ? "text-[#C6A15B]" : "text-black/80 hover:text-[#C6A15B]")
            }
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
            {label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex items-center gap-0.5 rounded-full border border-black/10 p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          role="radio"
          aria-checked={theme === value}
          aria-label={`${label} theme`}
          title={`${label} theme`}
          onClick={() => setTheme(value)}
          className={
            "inline-flex h-8 w-8 items-center justify-center rounded-full transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#C6A15B] " +
            (theme === value
              ? "bg-[#C6A15B]/15 text-[#C6A15B]"
              : "text-black/60 hover:text-[#C6A15B]")
          }
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      ))}
    </div>
  );
}
